import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/utils/api";

const CreateCertificatePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    your_name: "",
    partner_name: "",
    description: "",
  });
  const [wordCount, setWordCount] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Count words in description
    const words = formData.description.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [formData.description]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.your_name.trim()) {
      newErrors.your_name = "Your name is required";
    }
    
    if (!formData.partner_name.trim()) {
      newErrors.partner_name = "Partner's name is required";
    }
    
    if (!formData.description.trim()) {
      newErrors.description = "Certificate description is required";
    } else if (wordCount > 50) {
      newErrors.description = "Description must be 50 words or less";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors before continuing");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/certificates`, formData);
      
      toast.success("Certificate created! Redirecting to checkout...");
      
      // Navigate to checkout with certificate ID
      navigate(`/checkout/${response.data.id}`);
    } catch (error) {
      console.error("Error creating certificate:", error);
      toast.error(error.response?.data?.detail || "Failed to create certificate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="create-certificate-page">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8A0BF]/10 rounded-full text-sm text-[#C25E5E] mb-4">
              <Heart className="w-4 h-4 fill-[#C25E5E]" />
              <span>Create Your Certificate</span>
            </div>
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2D2D] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Create Your Love Certificate
            </h1>
            <p className="text-sm sm:text-base text-[#5A5A5A] max-w-md mx-auto">
              Fill in the details below to create your unique, timestamped proof of love.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-[#E2E2E2]/50">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Your Name */}
              <div className="space-y-2">
                <Label htmlFor="your_name" className="text-[#2D2D2D] font-medium text-sm sm:text-base">
                  Your Name *
                </Label>
                <Input
                  id="your_name"
                  name="your_name"
                  value={formData.your_name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className={`h-11 sm:h-12 border-[#E2E2E2] focus:border-[#E8A0BF] focus:ring-[#E8A0BF]/20 text-sm sm:text-base ${
                    errors.your_name ? "border-red-400" : ""
                  }`}
                  data-testid="input-your-name"
                />
                {errors.your_name && (
                  <p className="text-xs sm:text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    {errors.your_name}
                  </p>
                )}
              </div>

              {/* Partner's Name */}
              <div className="space-y-2">
                <Label htmlFor="partner_name" className="text-[#2D2D2D] font-medium text-sm sm:text-base">
                  Partner's Name *
                </Label>
                <Input
                  id="partner_name"
                  name="partner_name"
                  value={formData.partner_name}
                  onChange={handleChange}
                  placeholder="Enter your partner's name"
                  className={`h-11 sm:h-12 border-[#E2E2E2] focus:border-[#E8A0BF] focus:ring-[#E8A0BF]/20 text-sm sm:text-base ${
                    errors.partner_name ? "border-red-400" : ""
                  }`}
                  data-testid="input-partner-name"
                />
                {errors.partner_name && (
                  <p className="text-xs sm:text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    {errors.partner_name}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="description" className="text-[#2D2D2D] font-medium text-sm sm:text-base">
                    Certificate Description *
                  </Label>
                  <span className={`text-xs sm:text-sm ${wordCount > 50 ? "text-red-500" : "text-[#888888]"}`}>
                    {wordCount}/50 words
                  </span>
                </div>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Write a heartfelt message about your love story... (max 50 words)"
                  rows={4}
                  className={`border-[#E2E2E2] focus:border-[#E8A0BF] focus:ring-[#E8A0BF]/20 resize-none text-sm sm:text-base ${
                    errors.description ? "border-red-400" : ""
                  }`}
                  data-testid="input-description"
                />
                {errors.description && (
                  <p className="text-xs sm:text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Important Notice */}
              <div className="bg-[#F4F1EA] rounded-xl p-4 text-xs sm:text-sm text-[#5A5A5A]">
                <p className="font-medium text-[#2D2D2D] mb-2">Important:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Certificate content becomes permanent after creation</li>
                  <li>Double-check all details before proceeding</li>
                  <li>This is a symbolic certificate, not a legal document</li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6 py-5 sm:py-6 font-medium shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center gap-2 text-sm sm:text-base"
                data-testid="submit-certificate-btn"
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Creating...
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                    Continue to Payment
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-[#888888] text-center mt-6 max-w-md mx-auto">
            By creating a certificate, you agree to our{" "}
            <a href="/terms" className="text-[#E8A0BF] hover:underline">Terms & Conditions</a>
            {" "}and acknowledge our{" "}
            <a href="/disclaimer" className="text-[#E8A0BF] hover:underline">Disclaimer</a>.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateCertificatePage;
